import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Back from '../../components/common/Back';
import Icon from '../../components/common/Icon';
import PurposeStats from '../../components/common/PurposeStats';
import { apiErrorText, apiRequest, saveWithOptionalFile } from '../../utils';
import './MemberHome.css';
import './CompanyEvents.css';

/* What the company says it stands for — a name, a line saying what it means by it, and if it
   wants one a picture.

   Free text and not a pick from a list: two companies calling the same thing "Boldness" and "Lef"
   is the point, and the line under the word is where the second of them says what it means —
   because the word alone is the half every company writes and the half that says least.

   ★ NOT part of the engagement product. A company's values are the company itself, so every
   Bravos contact person reaches this screen whether they bought anything or not. What the product
   buys is the event half of the stats below, which is simply absent without it.

   Stored in data.PartnerValues and read back READ-ONLY on the Company Signal Record — which is
   why they are editable in exactly one place, this one. */

const EMPTY_VALUE = { id: '', name: '', description: '', image: '' };

const CompanyValues = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [values, setValues] = useState(null);
  const [draft, setDraft] = useState(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [params] = useSearchParams();

  const load = useCallback(() => {
    apiRequest('GET', '/bravos/company/purpose')
      .then((res) => {
        const rows = res.values || [];
        setValues(rows);
        /* ★ ?item= — the way back in from /purpose/value/<id>, the page behind a chip on the home
           badge. Landing on the right SCREEN is not enough: a company with thirty values would
           hand the admin the list they just came from and ask them to find the row again, so the
           item's own editor is opened, already filled in. Done here rather than in an effect of
           its own because it needs the rows this response carries. */
        const wanted = (params.get('item') || '').trim();
        if (wanted) {
          const found = rows.find(
            (v) => String(v.id).toLowerCase() === wanted.toLowerCase());
          if (found) { setDraft({ ...EMPTY_VALUE, ...found }); setFile(null); }
        }
      })
      .catch(() => navigate('/member-home'));
  }, [navigate, params]);

  useEffect(() => { load(); }, [load]);

  const save = () => {
    if (busy || !draft || !draft.name.trim()) return;
    setBusy(true);
    setNotice(null);
    saveWithOptionalFile('/bravos/company/value', draft, file)
      .then((res) => {
        setValues(res.values || []);
        setDraft(null);
        setFile(null);
      })
      .catch((err) => setNotice(apiErrorText(t, err)))
      .finally(() => setBusy(false));
  };

  const remove = (value) => {
    // eslint-disable-next-line no-alert
    if (busy || !window.confirm(t('deleteValueConfirm', { name: value.name }))) return;
    setBusy(true);
    apiRequest('DELETE', `/bravos/company/value/${value.id}`)
      .then((res) => setValues(res.values || []))
      .catch((err) => setNotice(apiErrorText(t, err)))
      .finally(() => setBusy(false));
  };

  const rows = values || [];

  return (
    <Base activeNav="home">
      <Card>
        <Back to="/member-home" />
        <Title my="1" className="mt-3">
          <Icon name="heart" theme="primary" className="me-2" />
          {t('ourValues')}
        </Title>

        {values === null ? (
          <span className="auth__spinner" />
        ) : (
          <>
            <p className="bravos-q__hint mb-2">{t('ourValuesNote')}</p>
            {rows.length === 0 && !draft && <p className="ce-note">{t('noValuesYet')}</p>}

            {rows.map((v) => (
              <div key={v.id} className="bravos-themerow">
                <span
                  className="bravos-themerow__img"
                  style={v.image ? { backgroundImage: `url("${v.image}")` } : undefined}
                />
                <span className="bravos-themerow__body">
                  <span className="bravos-themerow__name">{v.name}</span>
                  {/* The line under the word, when there is one. The "no picture" hint only gets
                      the slot while the company has nothing better to put in it. */}
                  {(v.description || !v.image) && (
                    <span className="bravos-themerow__when">
                      {v.description || t('valueNoImage')}
                    </span>
                  )}
                </span>
                <button
                  type="button"
                  className="bravos-member__makeadmin"
                  onClick={() => { setDraft({ ...EMPTY_VALUE, ...v }); setFile(null); }}
                >
                  {t('edit')}
                </button>
                <button
                  type="button"
                  className="bravos-member__remove"
                  onClick={() => remove(v)}
                  disabled={busy}
                  title={t('delete')}
                >
                  <Icon name="trash" />
                </button>
              </div>
            ))}

            {draft ? (
              <div className="bravos-form">
                <input
                  type="text"
                  value={draft.name}
                  maxLength={50}
                  placeholder={t('valueNamePlaceholder')}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                />
                {/* One line, not a paragraph: "Boldness" means something different at every
                    company, and this is the sentence that says which. Optional — a value with
                    only a word is still a value. */}
                <textarea
                  rows={2}
                  maxLength={300}
                  value={draft.description || ''}
                  placeholder={t('valueDescPlaceholder')}
                  onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                />
                <label className="bravos-form__label">
                  {t('valueImage')}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setFile(e.target.files[0] || null)}
                  />
                </label>
                <div className="bravos-form__row">
                  <button
                    type="button"
                    className="bravos-submit"
                    onClick={save}
                    disabled={busy || !draft.name.trim()}
                  >
                    {busy ? t('pleaseWait') : t('save')}
                  </button>
                  <button
                    type="button"
                    className="bravos-submit bravos-submit--ghost"
                    onClick={() => { setDraft(null); setFile(null); }}
                  >
                    {t('cancel')}
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                className="bravos-submit bravos-submit--ghost"
                onClick={() => { setDraft({ ...EMPTY_VALUE }); setFile(null); }}
              >
                <Icon name="plus" /> {t('addValue')}
              </button>
            )}
            {notice && <p className="bravos-cal__notice">{notice}</p>}
          </>
        )}
      </Card>

      <Card>
        <Title my="1">{t('purposeStatsTitle')}</Title>
        <PurposeStats kind="value" />
      </Card>
    </Base>
  );
};

export default CompanyValues;
